import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Mail,
  Phone,
  QrCode,
  User,
  UserCheck,
} from 'lucide-react';
import { apiClient } from '../utils/api';
import {
  getMaxDateForInput,
  isMinor,
  type SupportedPhoneCountry,
} from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from './InternationalPhoneInput';
import { useCurrentShop } from '../contexts/CurrentShopContext';
import { ALL_SHOPS_ID } from '../utils/currentShop';
import { DateInput } from './DateInput';
import { DateTimeInput } from './DateTimeInput';
import { localizeAppointmentApiMessage } from '../utils/localizeApiMessage';

interface QuickClientFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  isMinor: boolean | null;
  consentGiven: boolean;
}

interface LegalGuardianFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationshipToMinor: 'parent' | 'legal_guardian' | 'other';
  relationshipDetails: string;
}

type QuickAppointmentType = 'CONSULTATION' | 'TATTOO_SESSION' | 'PIERCING';

interface QuickAppointmentFormData {
  enabled: boolean;
  type: QuickAppointmentType;
  shopId: string;
  serviceId: string;
  practitionerId: string;
  scheduledAt: string;
}

interface SelectOption {
  id: string;
  name: string;
  price?: number;
  estimatedDuration?: number;
  category?: string;
}

interface QuickClientResponse {
  success: boolean;
  message: string;
  data?: {
    client: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    qrCode?: {
      id: string;
      url: string;
      dataUrl: string;
    };
    notifications?: {
      emailSent: boolean;
      smsSent: boolean;
    };
  };
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

interface AppointmentCreationResult {
  created: boolean;
  id?: string;
  error?: string;
}

interface QuickClientCreateFormProps {
  onSuccess?: (response: QuickClientResponse) => void;
  onCancel?: () => void;
}

const emptyLegalGuardian = (): LegalGuardianFormData => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  relationshipToMinor: 'parent',
  relationshipDetails: '',
});

const emptyAppointment = (): QuickAppointmentFormData => ({
  enabled: false,
  type: 'CONSULTATION',
  shopId: '',
  serviceId: '',
  practitionerId: '',
  scheduledAt: '',
});

const appointmentTypeToServiceCategory = (
  type: QuickAppointmentType,
): string => {
  if (type === 'TATTOO_SESSION') return 'TATTOO';
  if (type === 'PIERCING') return 'PIERCING';
  return 'CONSULTATION';
};

const appointmentTypeLabel = (type: QuickAppointmentType): string => {
  if (type === 'TATTOO_SESSION') return 'Tatouage';
  if (type === 'PIERCING') return 'Piercing';
  return 'Rendez-vous';
};

const addMinutesToDateTimeLocal = (
  dateTimeLocal: string,
  minutes: number,
): string => {
  if (!dateTimeLocal) return '';

  const date = new Date(dateTimeLocal);
  if (Number.isNaN(date.getTime())) return '';

  date.setMinutes(date.getMinutes() + Math.max(1, Math.round(minutes)));

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${mins}`;
};

const getLocalDateTimeMin = (): string => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${mins}`;
};

export function QuickClientCreateForm({
  onSuccess,
  onCancel,
}: QuickClientCreateFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentShopId } = useCurrentShop();

  const [formData, setFormData] = useState<QuickClientFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    isMinor: null,
    consentGiven: false,
  });

  const [addLegalGuardian, setAddLegalGuardian] = useState(false);
  const [legalGuardian, setLegalGuardian] =
    useState<LegalGuardianFormData>(emptyLegalGuardian);

  const [appointment, setAppointment] =
    useState<QuickAppointmentFormData>(emptyAppointment);
  const [shops, setShops] = useState<SelectOption[]>([]);
  const [services, setServices] = useState<SelectOption[]>([]);
  const [practitioners, setPractitioners] = useState<SelectOption[]>([]);
  const [appointmentOptionsLoading, setAppointmentOptionsLoading] =
    useState(false);
  const [appointmentOptionsError, setAppointmentOptionsError] = useState<
    string | null
  >(null);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] =
    useState<QuickClientResponse | null>(null);
  const [appointmentResult, setAppointmentResult] =
    useState<AppointmentCreationResult | null>(null);

  const [phoneCountry, setPhoneCountry] =
    useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  const [guardianPhoneCountry, setGuardianPhoneCountry] =
    useState<SupportedPhoneCountry>('FR');
  const [guardianPhoneDisplay, setGuardianPhoneDisplay] = useState('');
  const [guardianPhoneE164, setGuardianPhoneE164] = useState<string | null>(
    null,
  );
  const [guardianPhoneIsValid, setGuardianPhoneIsValid] = useState(false);

  const clientIsMinor = formData.isMinor;

  const filteredServices = useMemo(() => {
    const category = appointmentTypeToServiceCategory(appointment.type);
    return services.filter((service) => service.category === category);
  }, [services, appointment.type]);

  const selectedService = useMemo(
    () => services.find((service) => service.id === appointment.serviceId),
    [services, appointment.serviceId],
  );

  const selectedPractitioner = useMemo(
    () =>
      practitioners.find(
        (practitioner) => practitioner.id === appointment.practitionerId,
      ),
    [practitioners, appointment.practitionerId],
  );

  useEffect(() => {
    if (!appointment.enabled) return;
    if (shops.length > 0) return;

    const fetchShops = async () => {
      try {
        setAppointmentOptionsLoading(true);
        setAppointmentOptionsError(null);

        const response = await apiClient('/api/admin/shops?limit=1000');
        const data = await response.json();

        if (!data?.success) {
          setAppointmentOptionsError('Impossible de charger les shops.');
          return;
        }

        const nextShops: SelectOption[] = (data.data?.shops || []).map(
          (shop: any) => ({
            id: shop.id,
            name: shop.address?.city
              ? `${shop.name} - ${shop.address.city}`
              : shop.name,
          }),
        );

        setShops(nextShops);

        setAppointment((prev) => {
          if (prev.shopId) return prev;

          const currentIsUsable =
            currentShopId &&
            currentShopId !== ALL_SHOPS_ID &&
            nextShops.some((shop) => shop.id === currentShopId);

          const defaultShopId = currentIsUsable
            ? currentShopId
            : nextShops.length === 1
              ? nextShops[0].id
              : '';

          return defaultShopId ? { ...prev, shopId: defaultShopId } : prev;
        });
      } catch (error) {
        console.error('Quick create - fetch shops error:', error);
        setAppointmentOptionsError('Erreur de connexion lors du chargement des shops.');
      } finally {
        setAppointmentOptionsLoading(false);
      }
    };

    fetchShops();
  }, [appointment.enabled, shops.length, currentShopId]);

  useEffect(() => {
    if (!appointment.enabled || !appointment.shopId) {
      setServices([]);
      return;
    }

    const fetchServices = async () => {
      try {
        setAppointmentOptionsLoading(true);
        setAppointmentOptionsError(null);

        const response = await apiClient(
          `/api/admin/services?limit=1000&shopId=${encodeURIComponent(
            appointment.shopId,
          )}`,
        );
        const data = await response.json();

        if (!data?.success) {
          setServices([]);
          setAppointmentOptionsError('Impossible de charger les services.');
          return;
        }

        const nextServices: SelectOption[] = (data.data?.services || []).map(
          (service: any) => ({
            id: service.id,
            name: service.name,
            price: Number(service.basePrice || 0),
            estimatedDuration: Number(service.estimatedDuration || 60),
            category: service.category,
          }),
        );

        setServices(nextServices);
      } catch (error) {
        console.error('Quick create - fetch services error:', error);
        setServices([]);
        setAppointmentOptionsError(
          'Erreur de connexion lors du chargement des services.',
        );
      } finally {
        setAppointmentOptionsLoading(false);
      }
    };

    fetchServices();
  }, [appointment.enabled, appointment.shopId]);

  useEffect(() => {
    if (!appointment.enabled) return;

    const category = appointmentTypeToServiceCategory(appointment.type);
    const compatibleServices = services.filter(
      (service) => service.category === category,
    );

    setAppointment((prev) => {
      if (
        prev.serviceId &&
        compatibleServices.some((service) => service.id === prev.serviceId)
      ) {
        return prev;
      }

      return {
        ...prev,
        serviceId:
          compatibleServices.length === 1 ? compatibleServices[0].id : '',
        practitionerId: '',
      };
    });
  }, [appointment.enabled, appointment.type, services]);

  useEffect(() => {
    if (
      !appointment.enabled ||
      !appointment.shopId ||
      !appointment.serviceId
    ) {
      setPractitioners([]);
      return;
    }

    const fetchPractitioners = async () => {
      try {
        const [shopResponse, serviceResponse] = await Promise.all([
          apiClient(
            `/api/admin/shops/${encodeURIComponent(
              appointment.shopId,
            )}/practitioners`,
          ),
          apiClient(
            `/api/admin/services/${encodeURIComponent(
              appointment.serviceId,
            )}/practitioners`,
          ),
        ]);

        const [shopData, serviceData] = await Promise.all([
          shopResponse.json(),
          serviceResponse.json(),
        ]);

        if (!shopData?.success) {
          setPractitioners([]);
          return;
        }

        const shopPractitioners: SelectOption[] = (
          shopData.data?.practitioners || []
        )
          .filter((practitioner: any) => practitioner.isActive)
          .map((practitioner: any) => ({
            id: practitioner.practitionerId,
            name: `${practitioner.practitionerFirstName} ${practitioner.practitionerLastName}`.trim(),
          }));

        if (!serviceData?.success) {
          setPractitioners(shopPractitioners);
          return;
        }

        const allowedIds = new Set(
          (serviceData.data?.practitioners || [])
            .filter(
              (practitioner: any) =>
                practitioner.isOffered && practitioner.isAvailable,
            )
            .map((practitioner: any) => practitioner.id),
        );

        const compatiblePractitioners = shopPractitioners.filter(
          (practitioner) => allowedIds.has(practitioner.id),
        );

        setPractitioners(compatiblePractitioners);

        setAppointment((prev) => {
          if (
            prev.practitionerId &&
            !compatiblePractitioners.some(
              (practitioner) => practitioner.id === prev.practitionerId,
            )
          ) {
            return { ...prev, practitionerId: '' };
          }
          return prev;
        });
      } catch (error) {
        console.error('Quick create - fetch practitioners error:', error);
        setPractitioners([]);
      }
    };

    fetchPractitioners();
  }, [
    appointment.enabled,
    appointment.shopId,
    appointment.serviceId,
  ]);

  const handleInputChange = (
    field: keyof QuickClientFormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'dateOfBirth') {
        next.isMinor = typeof value === 'string' && value ? isMinor(value) : null;
      } else if (field === 'isMinor') {
        next.isMinor = value === true ? true : null;
      }
      return next;
    });

    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleGuardianChange = (
    field: keyof LegalGuardianFormData,
    value: string,
  ) => {
    setLegalGuardian((prev) => ({ ...prev, [field]: value }));

    setErrors((prev) => {
      const key = `legalGuardian.${field}`;
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleAppointmentChange = <K extends keyof QuickAppointmentFormData>(
    field: K,
    value: QuickAppointmentFormData[K],
  ) => {
    setAppointment((prev) => ({ ...prev, [field]: value }));

    const errorKey = `appointment.${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('quickCreate.validation.firstNameRequired');
    }

    const hasEmail = formData.email.trim().length > 0;
    const hasPhone = phoneDisplay.trim().length > 0;

    if (hasEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t('quickCreate.validation.emailInvalid');
      }
    }

    if (hasPhone && (!phoneIsValid || !phoneE164)) {
      newErrors.phone = t('quickCreate.validation.phoneInvalid');
    }

    if (clientIsMinor && addLegalGuardian) {
      if (
        legalGuardian.email.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(legalGuardian.email)
      ) {
        newErrors['legalGuardian.email'] = t(
          'quickCreate.validation.emailInvalid',
        );
      }

      if (guardianPhoneDisplay.trim() && (!guardianPhoneIsValid || !guardianPhoneE164)) {
        newErrors['legalGuardian.phone'] = t(
          'quickCreate.validation.phoneInvalid',
        );
      }
    }

    if (appointment.enabled) {
      if (!appointment.shopId) {
        newErrors['appointment.shopId'] = 'Veuillez sélectionner un shop.';
      }

      if (!appointment.serviceId) {
        newErrors['appointment.serviceId'] =
          'Veuillez sélectionner un service.';
      }

      if (!appointment.scheduledAt) {
        newErrors['appointment.scheduledAt'] =
          'Veuillez sélectionner une date et une heure.';
      } else {
        const appointmentDate = new Date(appointment.scheduledAt);
        if (
          Number.isNaN(appointmentDate.getTime()) ||
          appointmentDate.getTime() < Date.now()
        ) {
          newErrors['appointment.scheduledAt'] =
            'La date du rendez-vous doit être dans le futur.';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createAppointmentForClient = async (
    clientId: string,
  ): Promise<AppointmentCreationResult> => {
    const service = services.find(
      (candidate) => candidate.id === appointment.serviceId,
    );

    if (!service) {
      return {
        created: false,
        error: 'Le client a été créé, mais le service du rendez-vous est introuvable.',
      };
    }

    const durationMinutes = Math.max(
      1,
      Number(service.estimatedDuration || 60),
    );
    const basePrice = Number(service.price || 0);
    const estimatedEndTime = addMinutesToDateTimeLocal(
      appointment.scheduledAt,
      durationMinutes,
    );

    const reminderMethods: string[] = [];
    if (formData.email.trim()) reminderMethods.push('email');
    if (phoneE164) reminderMethods.push('sms');

    const appointmentData = {
      clientId,
      practitionerId: appointment.practitionerId || null,
      shopId: appointment.shopId,
      serviceId: appointment.serviceId,
      scheduledAt: appointment.scheduledAt,
      estimatedEndTime,
      status: 'SCHEDULED',
      type: appointment.type,
      notes: '',
      pricing: {
        basePrice,
        deposit: Math.round(basePrice * 0.5 * 100) / 100,
        total: basePrice,
        paymentMethod: 'card',
        currency: 'EUR',
      },
      preparation: {
        beforeAppointment: [],
        whatToBring: [],
        restrictions: [],
      },
      reminders: {
        enabled: reminderMethods.length > 0,
        methods: reminderMethods,
        daysBefore: [1, 7],
        customMessage: `Rappel: ${appointmentTypeLabel(
          appointment.type,
        )} le ${new Date(appointment.scheduledAt).toLocaleDateString('fr-FR')}`,
      },
    };

    try {
      const response = await apiClient('/api/admin/appointments', {
        method: 'POST',
        body: JSON.stringify(appointmentData),
      });

      const data = await response.json();

      if (!data?.success) {
        return {
          created: false,
          error:
            localizeAppointmentApiMessage(data?.message) ||
            'Le client a été créé, mais le rendez-vous n’a pas pu être créé.',
        };
      }

      const appointmentId =
        data?.data?.id || data?.data?.appointment?.id;

      if (appointmentId) {
        try {
          const confirmationResponse = await apiClient(
            `/api/admin/appointments/${appointmentId}/resend-confirmation-email`,
            { method: 'POST' },
          );

          const confirmationData = await confirmationResponse
            .json()
            .catch(() => null);

          if (!confirmationResponse.ok || !confirmationData?.success) {
            console.warn(
              'Appointment created, but confirmation email failed:',
              confirmationData?.message || confirmationData?.error,
            );
          }
        } catch (confirmationError) {
          console.warn(
            'Appointment created, but confirmation email failed:',
            confirmationError,
          );
        }
      } else {
        console.warn(
          'Appointment created, but no appointment id was returned for confirmation email.',
        );
      }

      return {
        created: true,
        id: appointmentId,
      };
    } catch (error) {
      console.error('Quick create appointment error:', error);
      return {
        created: false,
        error:
          'Le client a été créé, mais une erreur réseau a empêché la création du rendez-vous.',
      };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setErrors({});
      setAppointmentResult(null);

      const clientPayload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: phoneE164 ?? '',
        isMinor: formData.isMinor,
        consentGiven: formData.consentGiven,
        ...(formData.dateOfBirth
          ? { dateOfBirth: formData.dateOfBirth }
          : {}),
        legalGuardian:
          clientIsMinor && addLegalGuardian
            ? {
                ...legalGuardian,
                firstName: legalGuardian.firstName.trim(),
                lastName: legalGuardian.lastName.trim(),
                email: legalGuardian.email.trim(),
                phone: guardianPhoneE164 ?? '',
                relationshipDetails:
                  legalGuardian.relationshipDetails.trim(),
              }
            : undefined,
      };

      const response = await apiClient('/api/clients/quick-create', {
        method: 'POST',
        body: JSON.stringify(clientPayload),
      });

      const data: QuickClientResponse = await response.json();

      if (!data.success || !data.data?.client?.id) {
        if (data.errors) {
          const errorMap: Record<string, string> = {};
          data.errors.forEach((error) => {
            errorMap[error.field] = error.message;
          });
          setErrors(errorMap);
        } else {
          setErrors({
            general:
              data.message || 'Échec de la création du compte client.',
          });
        }
        return;
      }

      let createdAppointmentResult: AppointmentCreationResult | null = null;

      if (appointment.enabled) {
        createdAppointmentResult = await createAppointmentForClient(
          data.data.client.id,
        );
      }

      setSuccessData(data);
      setAppointmentResult(createdAppointmentResult);
      setShowSuccess(true);
      onSuccess?.(data);
    } catch (error) {
      console.error('Quick create error:', error);
      setErrors({ general: t('quickCreate.error.networkError') });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnother = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      isMinor: null,
      consentGiven: false,
    });

    setAddLegalGuardian(false);
    setLegalGuardian(emptyLegalGuardian());
    setAppointment(emptyAppointment());
    setPractitioners([]);
    setServices([]);
    setShowSuccess(false);
    setSuccessData(null);
    setAppointmentResult(null);
    setErrors({});

    setPhoneCountry('FR');
    setPhoneDisplay('');
    setPhoneE164(null);
    setPhoneIsValid(false);

    setGuardianPhoneCountry('FR');
    setGuardianPhoneDisplay('');
    setGuardianPhoneE164(null);
    setGuardianPhoneIsValid(false);
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (showSuccess && successData) {
    return (
      <div>
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>

          <h2 className="text-2xl font-display font-bold text-textPrimary mb-4">
            {t('quickCreate.success.title')}
          </h2>

          <p className="text-textSecondary mb-8">
            {t('quickCreate.success.message', {
              name: `${successData.data?.client.firstName} ${successData.data?.client.lastName}`,
            })}
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-textPrimary mb-4 flex items-center justify-center">
              <User className="w-5 h-5 mr-2" />
              {t('quickCreate.success.clientInfo')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div>
                <span className="text-sm text-gray-500">Nom complet</span>
                <p className="font-medium">
                  {successData.data?.client.firstName}{' '}
                  {successData.data?.client.lastName}
                </p>
              </div>

              {successData.data?.client.email && (
                <div>
                  <span className="text-sm text-gray-500">Email</span>
                  <p className="font-medium">
                    {successData.data.client.email}
                  </p>
                </div>
              )}

              {successData.data?.client.phone && (
                <div>
                  <span className="text-sm text-gray-500">Téléphone</span>
                  <p className="font-medium">
                    {successData.data.client.phone}
                  </p>
                </div>
              )}

              <div>
                <span className="text-sm text-gray-500">ID Client</span>
                <p className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                  {successData.data?.client.id}
                </p>
              </div>
            </div>
          </div>

          {appointmentResult?.created && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6 text-left">
              <div className="flex items-center text-blue-900 font-semibold mb-2">
                <Calendar className="w-5 h-5 mr-2" />
                Rendez-vous créé
              </div>
              <p className="text-sm text-blue-800">
                {appointmentTypeLabel(appointment.type)} prévu le{' '}
                {new Date(appointment.scheduledAt).toLocaleString('fr-FR')}.
              </p>
              {selectedPractitioner && (
                <p className="text-sm text-blue-800 mt-1">
                  Praticien : {selectedPractitioner.name}
                </p>
              )}
            </div>
          )}

          {appointmentResult && !appointmentResult.created && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6 text-left">
              <div className="flex items-center text-amber-900 font-semibold mb-2">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Client créé, rendez-vous non créé
              </div>
              <p className="text-sm text-amber-800">
                {appointmentResult.error}
              </p>
            </div>
          )}

          {successData.data?.qrCode && (
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-textPrimary mb-4 flex items-center justify-center">
                <QrCode className="w-5 h-5 mr-2" />
                {t('quickCreate.success.qrCode')}
              </h3>

              <div className="flex justify-center mb-4">
                <img
                  src={successData.data.qrCode.dataUrl}
                  alt="Client QR Code"
                  className="w-32 h-32 border border-gray-300 rounded-lg"
                />
              </div>

              <p className="text-sm text-blue-700">
                {t('quickCreate.success.qrCodeInstructions')}
              </p>
            </div>
          )}

          {successData.data?.notifications && (
            <div className="bg-green-50 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-green-800 mb-2">
                {t('quickCreate.success.notifications')}
              </h4>
              <div className="flex items-center justify-center space-x-4 text-sm">
                {successData.data.notifications.emailSent && (
                  <div className="flex items-center text-green-700">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span>{t('quickCreate.success.emailSent')}</span>
                  </div>
                )}

                {successData.data.notifications.smsSent && (
                  <div className="flex items-center text-green-700">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span>{t('quickCreate.success.smsSent')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={handleCreateAnother}
              className="btn-secondary"
            >
              {t('quickCreate.success.createAnother')}
            </button>

            {appointmentResult?.created && appointmentResult.id && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/appointments/${appointmentResult.id}/edit`)
                }
                className="btn-secondary"
              >
                Voir le rendez-vous
              </button>
            )}

            <button
              type="button"
              onClick={handleBackToDashboard}
              className="btn-primary"
            >
              {t('quickCreate.success.backToDashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <form onSubmit={handleSubmit} autoComplete="off" className="space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-800 text-sm">{errors.general}</span>
              </div>
            </div>
          )}

          {errors.contact && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-800 text-sm">{errors.contact}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('quickCreate.form.firstName')} *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  handleInputChange('firstName', e.target.value)
                }
                className={`input-field ${
                  errors.firstName
                    ? 'border-red-300 focus:ring-red-500'
                    : ''
                }`}
                placeholder={t('quickCreate.form.firstNamePlaceholder')}
              />
              {errors.firstName && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.firstName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('quickCreate.form.lastName')}{' '}
                <span className="font-normal text-gray-500">(optionnel)</span>
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) =>
                  handleInputChange('lastName', e.target.value)
                }
                className={`input-field ${
                  errors.lastName
                    ? 'border-red-300 focus:ring-red-500'
                    : ''
                }`}
                placeholder={t('quickCreate.form.lastNamePlaceholder')}
              />
              {errors.lastName && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.lastName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Date de naissance{' '}
                <span className="font-normal text-gray-500">(optionnel)</span>
              </label>
             <DateInput
                  value={formData.dateOfBirth ?? ''}
                  max={getMaxDateForInput()}
                  disabled={formData.isMinor === true && !formData.dateOfBirth}
                  onChange={(value) => handleInputChange('dateOfBirth', value)}
                  className="input-field"
                />
              <p className="mt-1 text-xs text-gray-500">
                Si elle est renseignée, le statut mineur est calculé automatiquement.
              </p>
              <label className="mt-3 flex items-center gap-2 text-sm text-textPrimary">
                <input
                  type="checkbox"
                  checked={formData.isMinor === true}
                  disabled={Boolean(formData.dateOfBirth)}
                  onChange={(e) => handleInputChange('isMinor', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                Mineur <span className="font-normal text-gray-500">(optionnel)</span>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email (optionnel)
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    handleInputChange('email', e.target.value)
                  }
                  className={`input-field ${
                    errors.email ? 'border-red-300 focus:ring-red-500' : ''
                  }`}
                  placeholder={t('quickCreate.form.emailPlaceholder')}
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Téléphone (optionnel)
                </label>
                <InternationalPhoneInput
                  value={phoneDisplay}
                  country={phoneCountry}
                  onChange={(result) => {
                    setPhoneDisplay(result.displayValue);
                    setPhoneCountry(result.country);
                    setPhoneE164(result.e164Value);
                    setPhoneIsValid(result.isValid);
                    setFormData((prev) => ({
                      ...prev,
                      phone: result.displayValue,
                    }));

                    if (result.isValid && errors.phone) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.phone;
                        return next;
                      });
                    }
                  }}
                  selectClassName="input-field"
                  inputClassName={`input-field ${
                    errors.phone ? 'border-red-300 focus:ring-red-500' : ''
                  }`}
                  placeholder={t('quickCreate.form.phonePlaceholder')}
                  error={errors.phone}
                />
              </div>
            </div>
          </div>

          {clientIsMinor && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 space-y-5">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={addLegalGuardian}
                  onChange={(e) => setAddLegalGuardian(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="flex items-center font-semibold text-amber-900">
                    <UserCheck className="mr-2 h-5 w-5" />
                    Ajouter un représentant légal
                  </span>
                  <span className="mt-1 block text-sm text-amber-800">
                    À renseigner si vous souhaitez enregistrer le responsable
                    légal dès maintenant.
                  </span>
                </span>
              </label>

              {addLegalGuardian && (
                <div className="grid grid-cols-1 gap-5 border-t border-amber-200 pt-5 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Prénom (optionnel)
                    </label>
                    <input
                      type="text"
                      value={legalGuardian.firstName}
                      onChange={(e) =>
                        handleGuardianChange('firstName', e.target.value)
                      }
                      className={`input-field ${
                        errors['legalGuardian.firstName']
                          ? 'border-red-300'
                          : ''
                      }`}
                    />
                    {errors['legalGuardian.firstName'] && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors['legalGuardian.firstName']}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Nom (optionnel)
                    </label>
                    <input
                      type="text"
                      value={legalGuardian.lastName}
                      onChange={(e) =>
                        handleGuardianChange('lastName', e.target.value)
                      }
                      className={`input-field ${
                        errors['legalGuardian.lastName']
                          ? 'border-red-300'
                          : ''
                      }`}
                    />
                    {errors['legalGuardian.lastName'] && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors['legalGuardian.lastName']}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Email (optionnel)
                    </label>
                    <input
                      type="email"
                      value={legalGuardian.email}
                      onChange={(e) =>
                        handleGuardianChange('email', e.target.value)
                      }
                      className={`input-field ${
                        errors['legalGuardian.email'] ? 'border-red-300' : ''
                      }`}
                    />
                    {errors['legalGuardian.email'] && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors['legalGuardian.email']}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Téléphone (optionnel)
                    </label>
                    <InternationalPhoneInput
                      value={guardianPhoneDisplay}
                      country={guardianPhoneCountry}
                      onChange={(result) => {
                        setGuardianPhoneDisplay(result.displayValue);
                        setGuardianPhoneCountry(result.country);
                        setGuardianPhoneE164(result.e164Value);
                        setGuardianPhoneIsValid(result.isValid);
                        handleGuardianChange('phone', result.displayValue);
                      }}
                      selectClassName="input-field"
                      inputClassName={`input-field ${
                        errors['legalGuardian.phone']
                          ? 'border-red-300'
                          : ''
                      }`}
                      error={errors['legalGuardian.phone']}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Relation avec le mineur *
                    </label>
                    <select
                      value={legalGuardian.relationshipToMinor}
                      onChange={(e) =>
                        handleGuardianChange(
                          'relationshipToMinor',
                          e.target.value,
                        )
                      }
                      className="input-field"
                    >
                      <option value="parent">Parent</option>
                      <option value="legal_guardian">Tuteur légal</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>

                  {legalGuardian.relationshipToMinor === 'other' && (
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Précisez la relation (optionnel)
                      </label>
                      <input
                        type="text"
                        value={legalGuardian.relationshipDetails}
                        onChange={(e) =>
                          handleGuardianChange(
                            'relationshipDetails',
                            e.target.value,
                          )
                        }
                        className={`input-field ${
                          errors['legalGuardian.relationshipDetails']
                            ? 'border-red-300'
                            : ''
                        }`}
                      />
                      {errors['legalGuardian.relationshipDetails'] && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors['legalGuardian.relationshipDetails']}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="rounded-xl border border-blue-200 bg-blue-50 p-5 space-y-5">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={appointment.enabled}
                onChange={(e) =>
                  setAppointment((prev) => ({
                    ...prev,
                    enabled: e.target.checked,
                  }))
                }
                className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />

              <span>
                <span className="flex items-center font-semibold text-blue-900">
                  <Calendar className="mr-2 h-5 w-5" />
                  Créer directement un rendez-vous
                </span>
                <span className="mt-1 block text-sm text-blue-800">
                  Le client sera créé en premier, puis le rendez-vous sera
                  automatiquement associé à sa fiche.
                </span>
              </span>
            </label>

            {appointment.enabled && (
              <div className="border-t border-blue-200 pt-5 space-y-5">
                {appointmentOptionsError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {appointmentOptionsError}
                  </div>
                )}

                {appointmentOptionsLoading && (
                  <p className="text-sm text-blue-700">
                    Chargement des disponibilités...
                  </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Type de rendez-vous *
                    </label>
                    <select
                      value={appointment.type}
                      onChange={(e) => {
                        const nextType = e.target.value as QuickAppointmentType;
                        setAppointment((prev) => ({
                          ...prev,
                          type: nextType,
                          serviceId: '',
                          practitionerId: '',
                        }));
                      }}
                      className="input-field"
                    >
                      <option value="CONSULTATION">Rendez-vous</option>
                      <option value="TATTOO_SESSION">Tatouage</option>
                      <option value="PIERCING">Piercing</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Shop *
                    </label>
                    <select
                      value={appointment.shopId}
                      onChange={(e) =>
                        setAppointment((prev) => ({
                          ...prev,
                          shopId: e.target.value,
                          serviceId: '',
                          practitionerId: '',
                        }))
                      }
                      className={`input-field ${
                        errors['appointment.shopId'] ? 'border-red-300' : ''
                      }`}
                    >
                      <option value="">Sélectionner un shop</option>
                      {shops.map((shop) => (
                        <option key={shop.id} value={shop.id}>
                          {shop.name}
                        </option>
                      ))}
                    </select>
                    {errors['appointment.shopId'] && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors['appointment.shopId']}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Service *
                    </label>
                    <select
                      value={appointment.serviceId}
                      onChange={(e) =>
                        handleAppointmentChange('serviceId', e.target.value)
                      }
                      className={`input-field ${
                        errors['appointment.serviceId'] ? 'border-red-300' : ''
                      }`}
                      disabled={!appointment.shopId}
                    >
                      <option value="">
                        {!appointment.shopId
                          ? 'Sélectionner d’abord un shop'
                          : filteredServices.length === 0
                            ? 'Aucun service compatible'
                            : 'Sélectionner un service'}
                      </option>
                      {filteredServices.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                    {errors['appointment.serviceId'] && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors['appointment.serviceId']}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Date et heure *
                    </label>
                  <DateTimeInput
                          value={appointment.scheduledAt}
                          min={getLocalDateTimeMin()}
                          onChange={(value) =>
                            handleAppointmentChange('scheduledAt', value)
                          }
                          className={
                            errors['appointment.scheduledAt']
                              ? '[&_input]:border-red-300'
                              : ''
                          }
                        />
                    {errors['appointment.scheduledAt'] && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors['appointment.scheduledAt']}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Praticien{' '}
                      <span className="font-normal text-gray-500">
                        (optionnel)
                      </span>
                    </label>
                    <select
                      value={appointment.practitionerId}
                      onChange={(e) =>
                        handleAppointmentChange(
                          'practitionerId',
                          e.target.value,
                        )
                      }
                      className="input-field"
                      disabled={
                        !appointment.shopId || !appointment.serviceId
                      }
                    >
                      <option value="">
                        {!appointment.serviceId
                          ? 'Sélectionner d’abord un service'
                          : 'Non assigné / assigner plus tard'}
                      </option>
                      {practitioners.map((practitioner) => (
                        <option key={practitioner.id} value={practitioner.id}>
                          {practitioner.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      La liste est filtrée selon le shop et le service choisis.
                    </p>
                  </div>

                  {selectedService && (
                    <div className="rounded-lg border border-blue-200 bg-white p-4 text-sm">
                      <p className="font-medium text-textPrimary mb-2">
                        Résumé du service
                      </p>
                      <p className="text-gray-600">
                        Durée estimée :{' '}
                        {selectedService.estimatedDuration || 60} min
                      </p>
                      <p className="text-gray-600">
                        Prix de base : {selectedService.price || 0}€
                      </p>
                      <p className="text-gray-600">
                        Acompte calculé :{' '}
                        {Math.round((selectedService.price || 0) * 0.5 * 100) /
                          100}
                        €
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="consent"
                checked={formData.consentGiven}
                onChange={(e) =>
                  handleInputChange('consentGiven', e.target.checked)
                }
                className={`h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5 ${
                  errors.consentGiven ? 'border-red-300' : ''
                }`}
              />

              <div className="flex-1">
                <label
                  htmlFor="consent"
                  className="text-sm font-medium text-textPrimary cursor-pointer"
                >
                  {t('quickCreate.form.consent')}{' '}
                  <span className="font-normal text-gray-500">(optionnel)</span>
                </label>
                <p className="text-sm text-textSecondary mt-1">
                  {t('quickCreate.form.consentDescription')}
                </p>
                {errors.consentGiven && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.consentGiven}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-6">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="btn-secondary flex-1"
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {appointment.enabled
                    ? 'Création du client et du rendez-vous...'
                    : t('quickCreate.form.creating')}
                </div>
              ) : appointment.enabled ? (
                'Créer le client et le rendez-vous'
              ) : (
                t('quickCreate.form.submit')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
